import "../css/primariesPage.scss";
import React, { useState, useContext, useRef } from "react";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";

import { quotesRef, restoresRef, timesheetsRef, jobsheetsRef } from "../firebase";

import { tooltips } from "../popData";

import { transferTimesheets, cloneTimesheets } from "../helperFunctions/dbFunctions";

import {
  Edit as EditIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";

import {
  AuthContext,
  SettingsContext,
  JobsheetsContext,
  SlidersContext,
  TimesheetsContext,
  QuotesContext,
} from "../providers";

import {
  Footer,
  FooterBtn,
  SliderWrapper,
  SliderContainer,
  StatusFilters,
  CloneOptions,
  Loading,
  SearchSortFilterBar,
  PrimarySection,
} from "../components";

import {
  useEditMode,
  useGlobalSearchData,
  useSliderControl,
  useViewWindowExtender,
  useSectionedData,
  useAddPrimary,
  useBatchPendingDraftsCheck,
} from "../customHooks";
import { useCallback } from "react";
import { defaultOptions } from "../helperFunctions/sortAndFilterFunctions";

const Jobsheets = () => {
  const auth = useContext(AuthContext);

  // Sliders >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  const containerRef = useRef(null);
  const { sliders, setSliderId, sliderId } = useContext(SlidersContext);

  // Determines how slider should react in different scenarios
  useSliderControl(sliders, setSliderId);
  // Sliders <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Source of primaries data >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  const { timesheets: jobsheets, loading } = useContext(JobsheetsContext);
  const { timesheets } = useContext(TimesheetsContext);
  const { timesheets: quotes } = useContext(QuotesContext);
  // Source of primaries data <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Sort and filter options for page >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  const [sortAndFilterOptions, setSortAndFilterOptions] = useState(() => defaultOptions("jobsheet"));
  // Sort and filter options for page <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Setup/dependencies for various functions defined in component >>>>>>>>>>>>>>>>>
  const history = useHistory();
  const addPrimary = useAddPrimary();

  const {
    currentTsNumber,
    currentQuoteNumber,
    incCurrentPrimaryNumber,
    vat,
    // currentJobsheetNumber,
  } = useContext(SettingsContext);

  const { inEditMode, toggleEditMode, isMarked, markMonth, markItem, markedItems, editMarkedItems } =
    useEditMode();
  // Setup/dependencies for various functions defined in component <<<<<<<<<<<<<<<<<

  // Format / Sort / Filter Data >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  // Primary data for page
  const sectionedTimesheets = useSectionedData(jobsheets, {
    ...sortAndFilterOptions,
    sortBy: "created",
  });

  // Opportunistically resolve any pending Gmail drafts on the most recent jobsheets
  // (mirrors this page's own sort - newest created first) whenever it's opened.
  const recentJobsheets = [...jobsheets]
    .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
    .slice(0, 10);
  useBatchPendingDraftsCheck(recentJobsheets);

  // Global search data
  let isSearching = sortAndFilterOptions?.searchTerm.length > 0;
  const globalSearchData = useGlobalSearchData(timesheets, quotes, sortAndFilterOptions, isSearching);

  let showGlobalSearch = isSearching && globalSearchData.length > 0;
  // Format / Sort / Filter data <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Enables infinite scroll like behaviour >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  const [endOfViewWindow] = useViewWindowExtender(
    jobsheets,
    sortAndFilterOptions,
    setSortAndFilterOptions,
    sectionedTimesheets,
    sliderId,
  );
  // Enable infinite scroll like behaviour <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Footer setup >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  let showFooter = true;
  let showBin = inEditMode && markedItems.length > 0;

  if (sliders && sliderId) {
    showFooter = false;
  }
  // Footer setup <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Functions >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  const editTimesheet = useCallback(
    (timesheet) => {
      let timesheetId = timesheet.uid;

      if (inEditMode) {
        markItem(timesheet);
      } else {
        if (sliders) {
          // If sliders are on, add chosen primary id to session storage

          setSliderId(timesheetId);
        }

        history.push(`/jobsheet/${timesheetId}`, { timesheet });
      }
    },
    [history, inEditMode, markItem, setSliderId, sliders],
  );

  async function mapView() {
    await toggleEditMode(document.body);
    let sheetsForMap = markedItems.map((primaryId) => {
      let lookingFor = jobsheets.find((ts) => {
        return primaryId === ts.uid;
      });

      return lookingFor;
    });

    history.push("/tom-tom-map", {
      sheets: sheetsForMap,
    });
  }

  async function deleteTimesheets() {
    await transferTimesheets(markedItems, jobsheetsRef, restoresRef, "jobsheet");
    editMarkedItems([]);
    toggleEditMode(document.body);
  }

  async function clone(e) {
    let cloneType = e.target.dataset.clonetype || "all";

    await cloneTimesheets(markedItems, cloneType, jobsheetsRef, jobsheetsRef, "jobsheet", vat);

    editMarkedItems([]);
    toggleEditMode(document.body);
  }

  async function convertToTimesheets() {
    await transferTimesheets(
      markedItems,
      jobsheetsRef,
      timesheetsRef,
      "timesheet",
      currentTsNumber,
      incCurrentPrimaryNumber,
    );
    editMarkedItems([]);
    toggleEditMode(document.body);
  }

  async function convertToQuotes() {
    await transferTimesheets(
      markedItems,
      jobsheetsRef,
      quotesRef,
      "quote",
      currentQuoteNumber,
      incCurrentPrimaryNumber,
    );
    editMarkedItems([]);
    toggleEditMode(document.body);
  }

  const addTimesheet = async function (e) {
    let result = await addPrimary("jobsheet", auth.user.uid);
    if (result?.success) {
      setSliderId(result?.uid);

      //addPrimary returns an object which lets you know if the operation failed or succeeded.
      //Use this to introduce useful error messgaes in the component.
    }
  };

  // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
  // Functions <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  if (loading) {
    return <Loading />;
  }

  return (
    <div ref={containerRef} className="primariesPage">
      <SearchSortFilterBar options={sortAndFilterOptions} setOptions={setSortAndFilterOptions} />

      <StatusFilters
        options={sortAndFilterOptions}
        setOptions={setSortAndFilterOptions}
        primaryType={"jobsheets"}
      />

      <SliderWrapper>
        <>
          {/* Primary Data >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> */}
          {/* Shows Jobsheets */}
          <div className={`primariesList ${inEditMode ? "dimSheets" : ""}`}>
            {sectionedTimesheets.map(({ title, data }) => {
              return (
                <PrimarySection
                  key={title}
                  title={title}
                  data={data}
                  markItem={markItem}
                  editPrimary={editTimesheet}
                  isMarked={isMarked}
                  primarySectionType={"jobsheet"}
                  markMonth={markMonth}
                />
              );
            })}

            <div ref={endOfViewWindow}></div>
          </div>
          {/* Primary Data <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}
          {/* Global Search Data >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> */}
          {/* Only show when searching: Timesheets & Quotes */}
          {showGlobalSearch && (
            <div className={`primariesList ${inEditMode ? "hide" : ""}`}>
              <PrimarySection
                title={"Timesheets & Quotes"}
                data={globalSearchData}
                markMonth={() => {}}
                editPrimary={(data) => {
                  history.push(`/${data.primaryType}/${data.uid}`, {
                    data,
                    preservePrimary: true,
                  });
                }}
                isMarked={() => {}}
                primarySectionType={"timesheet"}
              />
            </div>
          )}
          {/* Global Search Data <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}
        </>

        <SliderContainer primaryType={"jobsheet"} />
      </SliderWrapper>

      {showFooter && (
        <Footer back={false} title="Jobsheets" backTo={"/"}>
          <FooterBtn
            title={tooltips.edit}
            cb={() => toggleEditMode(document.body)}
            isActive={inEditMode}
          >
            <EditIcon style={{ fontSize: 25 }} />
          </FooterBtn>

          <FooterBtn title={tooltips.mapView} cb={mapView} show={showBin}>
            <LocationOnIcon style={{ fontSize: 30 }} />
          </FooterBtn>

          {showBin && <CloneOptions clone={clone} inEditMode={inEditMode} />}

          <FooterBtn title={tooltips.quoteConvert} cb={convertToQuotes} show={showBin}>
            <RestoreIcon title="Convert to Quotes" style={{ fontSize: 28 }} />
            <span>Q</span>
          </FooterBtn>

          <FooterBtn title={tooltips.timesheetConvert} cb={convertToTimesheets} show={showBin}>
            <RestoreIcon title="Convert to Timesheets" style={{ fontSize: 28 }} />
            <span>T</span>
          </FooterBtn>

          <FooterBtn title={tooltips.bin} cb={deleteTimesheets} show={showBin}>
            <DeleteOutlinedIcon style={{ fontSize: 30 }} />
          </FooterBtn>

          <FooterBtn title={tooltips.createJobsheet} cb={addTimesheet} show={!showBin}>
            <AddIcon style={{ fontSize: 30 }} />
          </FooterBtn>
        </Footer>
      )}
    </div>
  );
};

export default React.memo(Jobsheets);
